import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Link from 'components/Link'
import { Body14, Title60, Title42 } from 'components/Typography'
import Button from 'components/Button'
import { Spacing50, Container } from 'styles/sharedStyle'
import theme from 'styles/theme'
import GA4 from 'utils/GA4'
import scrollTo from 'utils/scrollTo'
import { useTheme } from 'styled-components'
import WhatsappIcon from 'components/Icons/WhatsappIcon'
import PhoneIcon from 'components/Icons/PhoneIcon'
import EmailIcon from 'components/Icons/EmailIcon'
import BookingUrlIcon from 'components/Icons/BookingUrlIcon'

import {
  Wrapper,
  Item,
  ItemList,
  ButtonWrapper,
  Header,
  Logo,
  ContactLogo,
  AddressText,
  ContactHeader,
  ContactTitle,
  ContactContainer,
  DirectionContainer,
  ContactDetailsContainer,
} from './ContactDetails.style'

const ContactDetails = ({
  headline,
  addressIcon,
  addressText,
  viewOnMapLabel,
  viewOnMapUrl,
  contactHeadline,
  whatsappLabel,
  whatsappContactLabels,
  whatsappContact,
  emailAddressLabel,
  emailContactLabels,
  emailContact,
  phoneNumberLabel,
  phoneContactLabels,
  phoneContact,
  bookingUrlLabel,
  bookingUrlContactLabels,
  bookingUrlContact,
  directionsFirst,
  greyBackground,
  villageIcons,
  villageName,
}) => {
  const [openedDropdown, setOpenedDropdown] = useState(null)

  const addressIconProps = {
    dangerouslySetInnerHTML: { __html: addressIcon?.code?.code },
  }
  const emailIconProps = {
    dangerouslySetInnerHTML: { __html: villageIcons?.email },
  }
  const phoneIconProps = {
    dangerouslySetInnerHTML: { __html: villageIcons?.phone },
  }
  const whatsappIconProps = {
    dangerouslySetInnerHTML: { __html: villageIcons?.whatsapp },
  }
  const bookingUrlIconProps = {
    dangerouslySetInnerHTML: { __html: villageIcons?.bookingUrl },
  }

  const onScrollTop = () => {
    scrollTo(0, 0)
  }

  const {
    colors: { black },
  } = useTheme()

  const hasCTA = !!(viewOnMapLabel && viewOnMapUrl)

  const handleOnClick = (contactType) => {
    GA4('contact_comp_contact_click', {
      contact_type: contactType,
      village_name: villageName,
    })
  }

  const renderDirectionsComponent = () => {
    return (
      <DirectionContainer>
        {headline ? (
          <Header
            greyBackground={greyBackground}
            as={directionsFirst ? Title60 : Title42}>
            {headline}
          </Header>
        ) : null}
        {addressIcon ? <Logo {...addressIconProps} color={black} /> : null}
        {addressText ? (
          <AddressText>{addressText?.addressText}</AddressText>
        ) : null}
        {hasCTA && (
          <>
            <ButtonWrapper>
              <Button
                as={Link}
                to={viewOnMapUrl}
                external={viewOnMapUrl.indexOf('http') === 0}
                onClick={() => {
                  onScrollTop()
                  GA4('contact_comp_get_directions', {
                    village_name: villageName,
                  })
                }}
                level="primary"
                fixedSize
                backgroundColor={greyBackground ? 'Grey' : ''}>
                {viewOnMapLabel}
              </Button>
            </ButtonWrapper>
          </>
        )}
      </DirectionContainer>
    )
  }

  const renderContactDetailsComponent = () => {
    return (
      <ContactDetailsContainer>
        {contactHeadline ? (
          <ContactHeader
            greyBackground={greyBackground}
            as={directionsFirst ? Title42 : Title60}>
            {contactHeadline}
          </ContactHeader>
        ) : null}
        <ItemList greyBackground={greyBackground}>
          {phoneContact && phoneNumberLabel && (
            <Item>
              <ContactTitle
                greyBackground={greyBackground}
                onClick={() => {
                  setOpenedDropdown(openedDropdown === 'phone' ? null : 'phone')
                  handleOnClick('Phone')
                }}>
                {villageIcons.phone ? (
                  <ContactLogo {...phoneIconProps} color={black} />
                ) : (
                  <PhoneIcon color={black} />
                )}
                <Body14 color={black} as="dt">
                  {phoneNumberLabel}
                </Body14>
              </ContactTitle>
              <ContactContainer isOpen={openedDropdown === 'phone'}>
                {phoneContact.length > 0 &&
                  phoneContact.map((contact, index) => (
                    <dd>
                      <Body14
                        style={{
                          wordWrap: 'break-word',
                          whiteSpace: 'pre-wrap',
                        }}
                        as={Link}
                        external
                        color={black}
                        to={`tel:${contact}`}>
                        {phoneContactLabels && phoneContactLabels[index]
                          ? `${phoneContactLabels[index]}: `
                          : ''}
                        {contact}
                      </Body14>
                    </dd>
                  ))}
              </ContactContainer>
            </Item>
          )}
          {emailContact && emailAddressLabel && (
            <Item>
              <ContactTitle
                greyBackground={greyBackground}
                onClick={() => {
                  setOpenedDropdown(openedDropdown === 'email' ? null : 'email')
                  handleOnClick('E-mail')
                }}>
                {villageIcons.email ? (
                  <ContactLogo {...emailIconProps} />
                ) : (
                  <EmailIcon color={black} />
                )}
                <Body14 color={black} as="dt">
                  {emailAddressLabel}
                </Body14>
              </ContactTitle>
              <ContactContainer isOpen={openedDropdown === 'email'}>
                {emailContact.length > 0 &&
                  emailContact.map((contact, index) => (
                    <dd>
                      <Body14
                        style={{
                          wordWrap: 'break-word',
                          whiteSpace: 'pre-wrap',
                        }}
                        color={black}
                        as={Link}
                        external
                        to={`mailto:${contact}`}>
                        {emailContactLabels && emailContactLabels[index]
                          ? `${emailContactLabels[index]}: `
                          : ''}
                        {contact}
                      </Body14>
                    </dd>
                  ))}
              </ContactContainer>
            </Item>
          )}
          {whatsappContact && whatsappLabel && (
            <Item>
              <ContactTitle
                greyBackground={greyBackground}
                onClick={() => {
                  setOpenedDropdown(
                    openedDropdown === 'whatsapp' ? null : 'whatsapp'
                  )
                  handleOnClick('WhatsApp')
                }}>
                {villageIcons.whatsapp ? (
                  <ContactLogo {...whatsappIconProps} />
                ) : (
                  <WhatsappIcon color={black} />
                )}
                <Body14 color={black} as="dt">
                  {whatsappLabel}
                </Body14>
              </ContactTitle>
              <ContactContainer isOpen={openedDropdown === 'whatsapp'}>
                {whatsappContact.length > 0 &&
                  whatsappContact.map((contact, index) => (
                    <dd>
                      <Body14
                        style={{
                          wordWrap: 'break-word',
                          whiteSpace: 'pre-wrap',
                        }}
                        as={Link}
                        color={black}
                        external
                        to={`https://wa.me/${contact.replace(/\s/g, '')}`}>
                        {whatsappContactLabels && whatsappContactLabels[index]
                          ? `${whatsappContactLabels[index]}: `
                          : ''}
                        {contact}
                      </Body14>
                    </dd>
                  ))}
              </ContactContainer>
            </Item>
          )}
          {bookingUrlContact && bookingUrlLabel && (
            <Item>
              <ContactTitle
                greyBackground={greyBackground}
                onClick={() => {
                  setOpenedDropdown(
                    openedDropdown === 'booking' ? null : 'booking'
                  )
                  handleOnClick('Booking Url')
                }}>
                {villageIcons.bookingUrl ? (
                  <ContactLogo {...bookingUrlIconProps} color={black} />
                ) : (
                  <BookingUrlIcon color={black} />
                )}
                <Body14 as="dt" color={black}>
                  {bookingUrlLabel}
                </Body14>
              </ContactTitle>
              <ContactContainer isOpen={openedDropdown === 'booking'}>
                {bookingUrlContact.length > 0 &&
                  bookingUrlContact.map((contact, index) => (
                    <dd>
                      <Body14
                        style={{
                          wordWrap: 'break-word',
                          whiteSpace: 'pre-wrap',
                        }}
                        as={Link}
                        external
                        color={black}
                        to={contact}>
                        {(bookingUrlContactLabels &&
                          bookingUrlContactLabels[index]) ||
                          contact}
                      </Body14>
                    </dd>
                  ))}
              </ContactContainer>
            </Item>
          )}
        </ItemList>
      </ContactDetailsContainer>
    )
  }

  return (
    <Wrapper greyBackground={greyBackground}>
      <Container p={`0 ${theme.space[8]}`} textAlign="center">
        <Spacing50 />
        {directionsFirst ? (
          <>
            {renderDirectionsComponent()}
            {renderContactDetailsComponent()}
          </>
        ) : (
          <>
            {renderContactDetailsComponent()}
            {renderDirectionsComponent()}
          </>
        )}
        <Spacing50 />
      </Container>
    </Wrapper>
  )
}

ContactDetails.propTypes = {
  headline: PropTypes.string,
  addressIcon: PropTypes.objectOf(PropTypes.string),
  addressText: PropTypes.string,
  viewOnMapLabel: PropTypes.string,
  viewOnMapUrl: PropTypes.string,
  contactHeadline: PropTypes.string,
  whatsappLabel: PropTypes.string,
  whatsappContactLabels: PropTypes.arrayOf(PropTypes.string),
  whatsappContact: PropTypes.arrayOf(PropTypes.string),
  emailAddressLabel: PropTypes.string,
  emailContactLabels: PropTypes.arrayOf(PropTypes.string),
  emailContact: PropTypes.arrayOf(PropTypes.string),
  phoneNumberLabel: PropTypes.string,
  phoneContactLabels: PropTypes.arrayOf(PropTypes.string),
  phoneContact: PropTypes.arrayOf(PropTypes.string),
  bookingUrlLabel: PropTypes.string,
  bookingUrlContactLabels: PropTypes.arrayOf(PropTypes.string),
  bookingUrlContact: PropTypes.arrayOf(PropTypes.string),
  directionsFirst: PropTypes.bool,
  greyBackground: PropTypes.bool,
  villageIcons: PropTypes.shape({
    email: PropTypes.string,
    whatsapp: PropTypes.string,
    phone: PropTypes.string,
    bookingUrl: PropTypes.string,
  }),
  villageName: PropTypes.string,
}

ContactDetails.defaultProps = {
  headline: '',
  addressIcon: null,
  addressText: '',
  viewOnMapLabel: '',
  viewOnMapUrl: '',
  contactHeadline: '',
  whatsappLabel: '',
  whatsappContactLabels: null,
  whatsappContact: [],
  emailAddressLabel: '',
  emailContactLabels: null,
  emailContact: [],
  phoneNumberLabel: '',
  phoneContactLabels: null,
  phoneContact: [],
  bookingUrlLabel: '',
  bookingUrlContactLabels: null,
  bookingUrlContact: [],
  directionsFirst: false,
  greyBackground: false,
  villageIcons: {},
  villageName: '',
}

export default ContactDetails
